import ControlPanel from '@/cam-admin/control-panel/ControlPanel';
import { type HandoverSpec } from '@/designs-dev/handover/types';
import { camIntakeScene } from '@/karehero-app/tell-us-once/CamIntake.scenes';

// The Overview tab, live. Same embedded control panel the CAM handovers use,
// opened on the tab this page is about, in one of the intake states.
//
// The states come from the scene catalog rather than being written out here, so
// this page and the state select on the CAM prototype offer the same three and
// an id that stops existing fails loudly.
const overview = (scene: 'none' | 'consented' | 'declined') => () => (
  <ControlPanel
    embedded
    initialTab='overview'
    intake={camIntakeScene(scene).initial.intake}
  />
);

/**
 * The CAM side of Tell Us Once: what a care expert picks up once a member has
 * been through the intake.
 *
 * Starts from what exists rather than from a blank page. The control panel
 * already has an Overview tab, and the surfaces around it — calls, documents,
 * actions, care search, notifications, caseloads — are all built and all have
 * flows pages of their own. So this page shows the overview we have and links to
 * the rest, and what gets drawn here is the delta the intake creates: facts
 * arriving before anybody has spoken to the member, and the review that goes
 * with them.
 */
export const camOverviewSpec: HandoverSpec = {
  id: 'cam-overview',
  title: 'CAM overview',
  // Desktop: the control panel is the CAM's surface and it is not a phone.
  screen: 'desktop',
  description:
    'What a care expert picks up after an intake. The control panel Overview tab as it stands, and the CAM surfaces around it that the intake will feed.',
  links: [
    {
      label: 'Control panel (live prototype)',
      url: '/cam-admin/control-panel',
    },
    { label: 'Care circles', url: '/cam-admin/care-circles' },
    { label: 'CAM notifications', url: '/cam-admin/notifications' },
    {
      label: 'Care search and shortlists (flows)',
      url: '/cam-admin/flows/cam-care-search',
    },
    {
      label: 'Notifications (flows)',
      url: '/cam-admin/flows/notifications',
    },
    {
      label: 'Caseloads and leave cover (flows)',
      url: '/cam-admin/flows/caseloads',
    },
    {
      label: 'Call notes, CAM side (flows)',
      url: '/meaningful-interactions/flows/calls',
    },
    {
      label: 'Documents, CAM side (flows)',
      url: '/meaningful-interactions/flows/documents',
    },
    { label: 'Actions (flows)', url: '/meaningful-interactions/flows/actions' },
    {
      label: 'Onboarding, the member side',
      url: '/tell-us-once/flows/intake-profile-review',
    },
  ],
  flows: [
    {
      title: 'The overview we have',
      steps: [
        {
          id: 'overview',
          title: 'Overview tab',
          tap: 'Control panel › Overview',
          render: overview('none'),
        },
      ],
    },
    {
      title: 'With an intake behind it',
      steps: [
        {
          id: 'overview-intake',
          title: 'Overview tab',
          tap: 'Control panel › Overview',
          render: overview('consented'),
        },
      ],
    },
  ],
  states: [
    {
      id: 'declined',
      title: 'They declined AI processing',
      kind: 'edge',
      repro:
        'Nothing on the circle was read from what the family sent, so the read-back does not exist and no fact is machine-filled',
      render: overview('declined'),
    },
  ],
  components: {},
};
